/**
 * different announcement conditions
 */
import type {
    AnnouncementDefault,
    AnnouncementReachVideo,
    AnnouncementVimeo,
    AnnouncementYoutube,
} from './Announcements';

export enum condition {
    OK = 'OK',
    PENDING = 'PENDING',
}

/**
 * Array of abbreviated weekdays
 * @todo: consider holistic approach to weekdays
 */
export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export type Weekdays = typeof WEEKDAYS[number];

/**
 * parse CMS date
 * @param date (format - h:mm A)
 * @throws {SyntaxError} Will throw `SyntaxError` if the format of the `date`
 * parameter is invalid.
 */
export function parseCmsDate(date: string): [0, number, number, 'AM' | 'PM'] {
    const exp = /^([0-9]{1,2})(?::([0-9]{2}))? +(PM|AM)$/;
    const matches = exp.exec(date);

    if (null != matches) {
        let hour = parseInt(matches[1]);
        let minute: number;

        if (typeof matches[2] !== 'undefined') {
            minute = parseInt(matches[2]);
        } else {
            minute = 0;
        }

        const amPm = matches[3] as 'AM' | 'PM';

        if (amPm == 'AM') {
            if (hour == 12) {
                hour = 0;
            }
        } else {
            if (hour != 12) {
                hour += 12;
            }
        }

        return [0, hour, minute, amPm];
    } else {
        throw new SyntaxError('Announcement.parseCmsDate: unexpected character');
    }
}

/**
 * return a CSS properties object
 * @param actualWidth - The actual width
 * @param actualHeight - The actual Height
 * @param compareWidth - Stage Width
 * @param compareHeight - Stage Height
 */
export function getWidgetScalePosition(
    actualWidth: number,
    actualHeight: number,
    compareWidth: number,
    compareHeight: number
): Pick<
    CSSStyleDeclaration,
    'width' | 'height' | 'transform' | 'transformOrigin' | 'overflow' | 'left' | 'top'
> {
    const widthRatio = actualWidth / compareWidth;
    const heightRatio = actualHeight / compareHeight;
    let zoomFactor: number;
    let topOffset: number;
    let leftOffset: number;

    if (widthRatio >= heightRatio) {
        zoomFactor = actualHeight / compareHeight;
        topOffset = 0;
        leftOffset = (actualWidth - zoomFactor * compareWidth) / 2;
    } else {
        //we must calculate the proper zoom factor
        zoomFactor = actualWidth / compareWidth;
        topOffset = (actualHeight - zoomFactor * compareHeight) / 2; //should calculate this value...
        leftOffset = 0;
    }

    return {
        width: `${compareWidth}px`,
        height: `${compareHeight}px`,
        // adding translateZ(0px) makes zone z-index settings work as expected
        transform: `scale(${zoomFactor}) translateZ(0px)`,
        transformOrigin: '0 0',
        overflow: 'hidden',
        left: `${leftOffset}px`,
        top: `${topOffset}px`,
    };
}

export type Announcement =
    | AnnouncementDefault
    | AnnouncementReachVideo
    | AnnouncementVimeo
    | AnnouncementYoutube;
